import { computed, inject, signal, Service } from '@angular/core';
import { MunicipalityWithOutage, PowerOutage } from '../../models/power-outage.model';
import { PowerOutageService } from '../../power-outage.service';

type InfoContainerView = 'idle' | 'municipality' | 'noOutage' | 'outage' | 'outsideElviaArea';

@Service()
export class InfoContainerStore {
  private readonly powerOutageService = inject(PowerOutageService);

  private readonly _selectedMunicipality = signal<MunicipalityWithOutage | undefined>(undefined);
  readonly selectedMunicipality = this._selectedMunicipality.asReadonly();

  private readonly _selectedOutage = signal<PowerOutage | undefined>(undefined);
  readonly selectedOutage = this._selectedOutage.asReadonly();

  private readonly _view = signal<InfoContainerView>('idle');
  readonly view = this._view.asReadonly();

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();

  setSelectedMunicipality(municipality: MunicipalityWithOutage | undefined) {
    this._selectedMunicipality.set(municipality);
  }

  setSelectedOutage(outageId: number | undefined) {
    const outage = this.powerOutageService.outages().find(o => o.id === outageId);
    this._selectedOutage.set(outage);
  }

  setView(view: InfoContainerView) {
    this._view.set(view);
  }

  setIsOpen(isOpen: boolean) {
    this._isOpen.set(isOpen);
  }

  resetAccordion() {
    this._isOpen.set(false);

    //using timeout with max transition duration to prevent content change before the transition ends
    //listening to transitionend is not reliable as the accordion seems to close instantly sometimes (bug in designsystem)
    //and therefore not trigger a transitionend event for its height change
    //see https://github.com/3lvia/designsystem/blob/8fbc0d689bf09769eebbff946b4260e8cb348364/packages/components/components/elvis-accordion/src/react/styledComponents.tsx#L158-L164
    setTimeout(() => {
      this.setSelectedOutage(undefined);
      this.setSelectedMunicipality(undefined);
      this.setView('idle');
    }, 700);
  }

  readonly municipalitiesWithOutages = computed(() => {
    const outages = this.powerOutageService.outages();

    const municipalityMap = new Map<string, MunicipalityWithOutage>();

    outages.forEach(outage => {
      const existingGroup = municipalityMap.get(outage.municipality);

      if (existingGroup) {
        //unplanned outages are prioritized, show red dot even if there are planned outages
        if (existingGroup.outageType !== 'Unplanned') {
          existingGroup.outageType = outage.type;
        }
        existingGroup.affectedCustomers += outage.affectedCustomers;
        existingGroup.outages.push(outage);
      } else {
        municipalityMap.set(outage.municipality, {
          name: outage.municipality,
          outages: [outage],
          outageType: outage.type,
          affectedCustomers: outage.affectedCustomers,
        });
      }
    });

    const municipalities = [...municipalityMap.values()];
    municipalities.forEach(group => {
      group.outages.sort((a, b) => a.city.localeCompare(b.city, 'nb-NO'));
    });

    return municipalities;
  });
}
